import type { ProGateOutcomeValue } from '@/utils/performanceTier';
import type { OptionProps } from '@amelio/ui-library';
import type { ReactNode } from 'react';

import { ProGateOutcome } from '@/utils/performanceTier';

export interface LockReadOnlyActionsParams {
    /** The row's subject can only be read: the company lost the Performance PRO it needs. */
    isReadOnly: boolean;
    /** Whether the reader is shown Performance PRO locks (see `resolveProGate`). */
    gate: ProGateOutcomeValue | undefined;
    lockIcon: ReactNode;
    onLockedClick: () => void;
    /** The actions that change the row's subject; the others (viewing) are left as they are. */
    isLockable: (action: OptionProps) => boolean;
}

/**
 * A row menu for something the company can only read. Those shown Performance PRO locks keep the
 * actions that would change it, locked: choosing one offers the upgrade. Everyone else does not
 * see them. A row that is not read-only keeps its menu as is.
 */
export const lockReadOnlyActions = (
    actions: OptionProps[],
    { isReadOnly, gate, lockIcon, onLockedClick, isLockable }: LockReadOnlyActionsParams,
): OptionProps[] => {
    if (!isReadOnly) {
        return actions;
    }

    if (gate !== ProGateOutcome.Lock) {
        return actions.filter((action) => !isLockable(action));
    }

    return actions.map((action) =>
        isLockable(action)
            ? {
                  ...action,
                  icon: lockIcon,
                  danger: false,
                  onClick: onLockedClick,
                  dataTestId: `${action.id}-locked`,
              }
            : action,
    );
};
